import { useEffect, useState } from "react";
import { api } from "./api";
import { Btn, Field, TextInput, useSaveState } from "./atoms";

/* ============================================================
   /admin/transactional — Transactional email settings
   Minimal UI for now: the "info@" sender used for the Contact
   form, "Get started" form, and any future general
   transactional emails. The 2FA sender is env-only and
   shown read-only so the admin knows what's wired.
============================================================ */

export default function TransactionalPage() {
  const [settings, setSettings] = useState(null);
  const [draftFrom, setDraftFrom] = useState("");
  const { saving, saved, error, wrap } = useSaveState();

  const load = async () => {
    const r = await api.emailSettingsGet();
    setSettings(r);
    setDraftFrom(r.from_general || "");
  };

  useEffect(() => {
    load();
  }, []);

  if (!settings) return <div className="text-sm text-neutral-500">Loading…</div>;

  const dirty = (draftFrom || "").trim().toLowerCase() !== (settings.from_general || "").toLowerCase();

  const onSave = () =>
    wrap(async () => {
      await api.emailSettingsUpdate({ from_general: draftFrom.trim() });
      await load();
    });

  const onResetToDefault = () =>
    wrap(async () => {
      await api.emailSettingsUpdate({ from_general: "" });
      await load();
    });

  return (
    <div data-testid="admin-transactional-page" className="pb-20 max-w-3xl">
      <div className="mb-9">
        <h1 className="font-display text-3xl font-black tracking-[-0.025em]">
          Transactional email
        </h1>
        <p className="mt-2 text-sm text-neutral-400 max-w-2xl leading-[1.6]">
          Sender addresses for emails the site sends automatically — Contact form
          replies, Get-started form, admin notifications. Future topics (forms,
          templates, provider settings) will live here too.
        </p>
      </div>

      {!settings.api_key_set ? (
        <div className="mb-8 rounded-md border border-red-500/30 bg-red-500/[0.06] px-5 py-4 text-[0.92rem] text-red-200">
          <div className="font-bold text-white">Email provider not configured.</div>
          <div className="mt-1 text-neutral-300">
            The `RESEND_API_KEY` environment variable is missing on the backend.
            Ask your developer to add it — without it, no email will be sent
            (including 2FA codes).
          </div>
        </div>
      ) : null}

      {/* General sender */}
      <section className="rounded-xl border border-white/[0.08] bg-[#0F141A] p-6 mb-6">
        <div className="flex items-center justify-between gap-4 mb-5">
          <div>
            <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">
              General sender
            </div>
            <h2 className="mt-2 font-display text-xl font-black tracking-[-0.015em] text-white">
              Contact form · Get started · Transactional
            </h2>
          </div>
          {settings.from_general_override ? (
            <span className="text-[0.6rem] font-bold tracking-[0.22em] uppercase px-2 py-1 rounded bg-[#F4D64E]/[0.15] text-[#F4D64E]">
              Override active
            </span>
          ) : (
            <span className="text-[0.6rem] font-bold tracking-[0.22em] uppercase px-2 py-1 rounded bg-white/[0.05] text-neutral-500">
              Using default
            </span>
          )}
        </div>

        <Field
          label="Sender email"
          hint="Must be on a domain you've verified in Resend. Leave blank and save to revert to the default (the value of RESEND_FROM_GENERAL in backend env)."
        >
          <TextInput
            value={draftFrom}
            onChange={setDraftFrom}
            placeholder="info@sundaydigital.io"
            data-testid="transactional-from-general"
          />
        </Field>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Btn onClick={onSave} disabled={!dirty || saving} data-testid="transactional-save">
            {saving ? "Saving…" : "Save changes"}
          </Btn>
          {settings.from_general_override ? (
            <Btn
              variant="ghost"
              onClick={onResetToDefault}
              disabled={saving}
              data-testid="transactional-reset"
            >
              Reset to default
            </Btn>
          ) : null}
          {saved ? <span className="text-[0.7rem] text-[#F4D64E]">Saved ✓</span> : null}
          {error ? <span className="text-[0.78rem] text-red-400">{error}</span> : null}
        </div>
      </section>

      {/* 2FA sender — read-only */}
      <section className="rounded-xl border border-white/[0.08] bg-[#0F141A] p-6 mb-6">
        <div className="flex items-center justify-between gap-4 mb-5">
          <div>
            <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
              Admin 2FA sender · Read-only
            </div>
            <h2 className="mt-2 font-display text-xl font-black tracking-[-0.015em] text-white">
              Security verification codes
            </h2>
            <p className="mt-2 text-[0.85rem] text-neutral-400 max-w-[52ch] leading-[1.6]">
              The 2FA sender is intentionally locked down and can only be
              changed via the `RESEND_FROM_2FA` environment variable. This
              protects the admin login flow from being misconfigured.
            </p>
          </div>
        </div>
        <Field label="2FA sender">
          <TextInput value={settings.from_2fa || ""} onChange={() => {}} disabled />
        </Field>
      </section>

      {/* Coming soon */}
      <section className="rounded-xl border border-dashed border-white/[0.08] p-6 text-center">
        <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-neutral-500 mb-2">
          Coming soon
        </div>
        <p className="text-sm text-neutral-400 max-w-md mx-auto leading-[1.6]">
          Templates, per-form routing, reply-to addresses, send logs — coming
          to this screen as the site grows.
        </p>
      </section>
    </div>
  );
}
